<template>
  <SectionFrame v-if="packages.length || inStudio" :section="section" :index="index" :heading="heading" label="Packages">
    <template v-if="packages.length">
      <table v-if="layout === 'table'" class="s2-pack-table t-body mo-reveal">
        <caption class="s2-hp">{{ heading || "Packages" }}</caption>
        <thead><tr><th scope="col"><span class="s2-hp">What's included</span></th><th v-for="p in packages" :key="p.name" scope="col" :data-highlight="p.highlight">
          <span class="t-h3">{{ p.name }}</span><div class="t-price">{{ p.price }}</div></th></tr></thead>
        <tbody>
        <tr v-for="f in allFeatures" :key="f"><th scope="row">{{ f }}</th>
          <td v-for="p in packages" :key="p.name" :data-highlight="p.highlight">
            <span v-if="p.features.includes(f)" aria-label="Included">✓</span><span v-else aria-label="Not included" class="s2-muted">—</span></td></tr>
        </tbody>
      </table>
      <div v-else class="s2-grid s2-packs mo-stagger" :style="{'--s2-cols': layout === 'two' ? 2 : Math.min(3, packages.length)}">
        <article v-for="p in packages" :key="p.name" class="t-card s2-pack" :data-highlight="p.highlight">
          <span v-if="p.highlight" class="t-badge s2-pack-badge">Most popular</span>
          <h3 class="t-h3">{{ p.name }}</h3>
          <p v-if="p.price" class="t-price s2-pack-price">{{ p.price }}</p>
          <p v-if="p.note" class="s2-muted">{{ p.note }}</p>
          <ul class="s2-ticks"><li v-for="f in p.features" :key="f">{{ f }}</li></ul>
          <a class="t-btn" :class="p.highlight ? '' : 't-btn--ghost'" :href="back(p.link || '/contact')">{{ p.button || "Get a quote" }}</a>
        </article>
      </div>
      <p v-if="section.disclaimer" class="s2-note">{{ section.disclaimer }}</p>
    </template>
    <StudioGap v-else>Add two or three packages with what's included.</StudioGap>
  </SectionFrame>
</template>

<script setup>
import {computed} from "vue";
import SectionFrame from "./SectionFrame.vue";
import StudioGap from "./StudioGap.vue";
import {layoutOf, packageList} from "@/utils/sectionLogic.js";
import {studioMode} from "@/classes/previewBridge.js";
import {useBackLinks} from "@/composables/useBackLinks.js";
const back = useBackLinks();

const props = defineProps({section: {type: Object, required: true}, index: {type: Number, default: 0}});
const inStudio = computed(() => studioMode.value !== "off");
const layout = computed(() => layoutOf(props.section, ["three", "two", "table"]));
const heading = computed(() => props.section.heading ?? "");
const packages = computed(() => packageList(props.section).slice(0, layout.value === "two" ? 2 : 4));
const allFeatures = computed(() => [...new Set(packages.value.flatMap((p) => p.features))]);
</script>

<style>
.s2-pack { position: relative; display: flex; flex-direction: column; gap: .5rem; }
.s2-pack[data-highlight="true"] { box-shadow: 0 0 0 2px var(--t-accent), var(--t-card-shadow); }
.s2-pack .t-btn { margin-top: auto; align-self: flex-start; }
.s2-pack-badge { position: absolute; top: -.8rem; right: 1rem; }
.s2-pack-price { font-size: 1.6rem; margin: 0; }
.s2-ticks { list-style: none; padding: 0; margin: .5rem 0 1rem; }
.s2-ticks li { padding: .3rem 0 .3rem 1.6rem; position: relative; }
.s2-ticks li::before { content: "✓"; position: absolute; left: 0; color: var(--t-accent); font-weight: 700; }
.s2-pack-table { width: 100%; border-collapse: collapse; }
.s2-pack-table th, .s2-pack-table td { padding: .7rem .5rem; border-bottom: 1px solid var(--t-line); text-align: center; font-weight: 400; }
.s2-pack-table tbody th { text-align: left; }
.s2-pack-table [data-highlight="true"] { background: color-mix(in srgb, var(--t-accent) 8%, transparent); }
@media (max-width: 640px) { .s2-pack-table { font-size: .85rem; } }
</style>
